import {Alert, Button, DatePicker, Form, Input, message, Row, Select, Spin, Tooltip} from 'antd';
import {CopyOutlined, EyeOutlined, PlusOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {Formik} from 'formik';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  brandConfig,
  CampaignDetailsProps,
  CONSTANTS,
  FormRow,
  generateAlphabetString,
  isPaidClient,
} from '../../../../ui/src';
import {CampaignTypes} from '../../../../types/src';
import {ConfirmationModal, InfoIcon, LockIcon, MessageLengthCount, MessageTextArea} from '../';
import {isContainLink, isHebrew, STRINGS} from '../..';
import {CsvInput} from '../csvInput/csvInput';
import {
  campaignStartTimeTooltip,
  disablePastCampaignStartDate,
  disablePastCampaignStartTime,
  getAdvancedMessages,
  getIntegratedCampaignTooltip,
  RemoveFollowUpButton,
  SearchFilterLabel,
  shouldShowCampaignStartTimeField,
} from './common';
import {UrlFilters} from './urlFilters';
import {AcquisitionSchema} from './validationSchemas';
import './AcquisitionDetails.scss';
import {getSelectedTypeWarningIfNeeded} from './getSelectedTypeWarningIfNeeded';
import {AdvancedOptions} from './advancedOptions';
import {LeadFiltrationField} from './common/LeadFiltrationField';
import {SelectAudienceListFilters} from './common/SelectAudienceListFilters';
import {SelectAudienceList} from './common/SelectAudienceList';
import {SelectEnrichmentList} from './common/SelectEnrichmentList';
import {SelectEnrichmentListFilters} from './common/SelectEnrichmentListFilters';

const {Option, OptGroup} = Select;

const campaignTypesWithUrl = [
  CampaignTypes.Acquisition,
  CampaignTypes.AcquisitionCsvLikedPost,
  CampaignTypes.AcquisitionInmail,
];

export const AcquisitionDetails = ({
  campaign,
  campaignList,
  client,
  clientAttachments,
  deleteClientAttachment,
  campaignProcessing,
  trackIntercomEvent,
  apiUrl,
  getTags,
  onSubmit,
  onCancel,
  isCSVInvalid,
  error,
  onParseSearchUrl,
  campaignParsedFilters,
  campaignParsedFiltersLoading,
  resetSearchUrlFilters,
  templates,
  createTemplate,
  moveFromPromoPlan,
  getFollowupMessageSuggestion,
  isInstantlyVerified,
  rb2bLatestEvent,
  setHeaderModal,
  InstantlyFormItemContainer,
  clientTags,
  isAdmin,
  loadMoreLeadLists,
  getLeadFilters,
  clearFilters,
  loadMoreEnrichmentLists,
  getEnrichmentFilters,
  clearEnrichmentFilters,
  instantly_campaigns,
}: CampaignDetailsProps) => {
  const formikRef: any = useRef(undefined);
  const [isFiltersChanged, setIsFiltersChanged] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [selectedType, setSelectedType] = useState(undefined);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [second_followup_message_visible, setSecondFollowupMessageVisible] = useState(false);
  const [third_followup_message_visible, setThirdFollowupMessageVisible] = useState(false);

  // TODO WHAT IS THIS??
  const [tracking_link_prefix, setTrackingLinkPrefix] = useState(
    campaign.id ? campaign.tracking_link_prefix : generateAlphabetString()
  );

  const day_options = Array(30)
    .fill(0)
    .map((_, idx) => {
      const key = idx;
      return {key, label: key === 0 ? 'No delay' : key.toString()};
    });

  const defaultDaysDelay = '7';

  // TODO move it?
  useEffect(() => {
    const newType = client.campaign_sources_limited ? CONSTANTS.campaignTypes.acquisition_basic : campaign.type;
    setSelectedType(newType);
    formikRef.current?.setFieldValue('type', newType);
    setSecondFollowupMessageVisible(!!campaign.second_followup_message);
    setThirdFollowupMessageVisible(!!campaign.third_followup_message);

    if (campaign.filters) {
      onParseSearchUrl(campaign.filters);
    }
  }, [campaign, getTags, onParseSearchUrl]);

  // TODO move it?
  const getCsvFileLink = () => {
    return campaign.csv ? `${apiUrl}api/v2/campaigns/${campaign.id}/download-csv` : null;
  };

  const handleSubmit = async (campaignForm: any) => {
    if (
      campaignForm.type === 'acquisition_csv' ||
      campaignForm.followup_attachment ||
      campaignForm.second_followup_attachment ||
      campaignForm.third_followup_attachment
    ) {
      if (campaignForm.csv && campaignForm.type === 'acquisition_csv' && (await isCSVInvalid(campaignForm.csv))) return;
      setProcessingMessage(
        campaignForm.followup_attachment ||
          campaignForm.second_followup_attachment ||
          campaignForm.third_followup_attachment
          ? 'Uploading file, please wait.'
          : 'CSV processing will take a while, please, wait for the ending.'
      );
    }

    if (campaignForm.type === CampaignTypes.AcquisitionCsvAudienceList) {
      campaignForm.audience_list_filters_ids = JSON.stringify(campaignForm.audience_list_filters_ids);
    }

    if (campaignForm.type === CampaignTypes.AcquisitionCsvEnrichment) {
      campaignForm.enrichment_filters_ids = JSON.stringify(campaignForm.enrichment_filters_ids);
    }

    await onSubmit(
      {
        ...campaign,
        ...campaignForm,
      },
      {second_followup_message_visible, third_followup_message_visible}
    );
  };

  const resetFields = setFieldValue => {
    setFieldValue('filters', '');
    setFieldValue('csv', null);
    setFieldValue('csv_name', '');
    resetSearchUrlFilters();
  };

  const handleSwitchCampaignType = (selected, setFieldValue) => {
    resetFields(setFieldValue);
    setFieldValue('type', selected);
    setSelectedType(selected);
    if (selected === CampaignTypes.AcquisitionBasic) {
      // basic li campaign doesn't have invite message
      setFieldValue('message', '');
    }
    const newWebhookToken = [CampaignTypes.AcquisitionCsvWebhook, CampaignTypes.AcquisitionCsvVector].includes(selected)
      ? uuidv4()
      : undefined;
    setFieldValue('webhook_token', newWebhookToken);
    const newDataExtractorString = selected === CampaignTypes.AcquisitionCsvVector ? 'linkedinUrl' : '';
    setFieldValue('linkedin_url_data_extractor_string', newDataExtractorString);
  };

  const onClickCsvRequest = () => {
    if (!client.csv_campaigns_feature) {
      trackIntercomEvent('csv_campaigns_feature_request', true, true);
      message.info('Your request sent.');
    }
  };

  const onClose = () => setConfirmationVisible(true);
  const onCloseConfirmModal = () => setConfirmationVisible(false);
  const onCloseModals = () => {
    setConfirmationVisible(false);
    onCancel();
  };

  const getInviteMaxLength = (selectedType, message) => {
    if (selectedType === 'acquisition_inmail') {
      return CONSTANTS.limits.inmail_message_length;
    } else if (isHebrew(message)) {
      return CONSTANTS.limits.hebrew_invite_message;
    } else if (client.signature_enabled) {
      return CONSTANTS.limits.invite_message_whitelabel_limit;
    }
    return CONSTANTS.limits.invite_message;
  };

  const canUpdateCSVFile = !!campaign.id && !!campaign.csv_name;
  const beforeUploadAttachment = (setFieldValue: any, field, value: any) => {
    setFieldValue(field, value);
  };

  const handleSwitchTextarea = (checked, key, setFieldValue) => {
    const attachmentKey = key.replace('_message', '_attachment');
    if (key === 'second_followup_message') {
      setSecondFollowupMessageVisible(!!checked);
    } else if (key === 'third_followup_message') {
      setThirdFollowupMessageVisible(!!checked);
    }
    setFieldValue(key, '');
    setFieldValue(attachmentKey, '');
  };

  const getTooltipMessage = type => {
    let message;
    let followup_message;
    switch (type) {
      case 'acquisition_message_request':
        message = STRINGS.tooltip.message_request_message;
        followup_message = STRINGS.tooltip.followup_message;
        break;
      case 'acquisition_inmail':
        message = STRINGS.tooltip.inmail_message;
        followup_message = STRINGS.tooltip.inmail_followup_message;
        break;
      default:
        message = STRINGS.tooltip.invite_message;
        followup_message = STRINGS.tooltip.followup_message;

        break;
    }
    return {message, followup_message};
  };

  const onSuggestMessageTemplate = async (type, values, setValue, valueName) => {
    const result = await getFollowupMessageSuggestion({
      campaign_name: values.name,
      search_filter_url: values.filters,
      initial_message: values.message,
      first_message: values.followup_message,
      second_message: values.second_followup_message,
      suggest_message_type: type,
    });
    if (result) {
      setValue(valueName, result);
    }
  };

  const shouldShowSuggestionButton = !campaign.id && client.ai_followup_generation_feature;
  const isCampaignExist = !!campaign.id;
  const shouldShow = ![
    CampaignTypes.AcquisitionViaImport,
    CampaignTypes.AcquisitionMessageRequest,
    CampaignTypes.AcquisitionInmail,
  ].includes(selectedType);
  const isRb2bCampaignExist = !!campaignList.find(c => c.type === CampaignTypes.AcquisitionCsvRb2b);
  const isSquidCampaignExist = !!campaignList.find(c => c.type === CampaignTypes.AcquisitionCsvSquid);
  const isInstantlyCampaignExist = !!campaignList.find(c => c.type === CampaignTypes.AcquisitionCsvInstantly);
  const isEnrichmentCampaignExist = !!campaignList.find(c => c.type === CampaignTypes.AcquisitionCsvEnrichment);

  return (
    <Spin spinning={campaignProcessing} tip={processingMessage}>
      <ConfirmationModal visible={confirmationVisible} onOk={onCloseModals} onCancel={onCloseConfirmModal} />
      <Formik
        innerRef={formikRef}
        initialValues={{
          name: campaign.name,
          start_time: campaign.start_time || null,
          filters: campaign.filters,
          message: campaign.message,
          in_mail_subject: campaign.in_mail_subject,
          linkedin_url_data_extractor_string: campaign.linkedin_url_data_extractor_string,
          webhook_token: campaign.webhook_token,
          followup_message: campaign.followup_message,
          second_followup_message: campaign.second_followup_message,
          exclude_companies: campaign.exclude_companies,
          exclude_responders: campaign.exclude_responders,
          enhance_active: campaign.enhance_active,
          multiple_invites_active: campaign.multiple_invites_active,
          twitter_follow: campaign.twitter_follow,
          followup_days: campaign.followup_days || 0,
          second_followup_days: campaign.second_followup_days || defaultDaysDelay,
          type: campaign.type,
          id: campaign.id,
          csv: null,
          csv_name: campaign.csv_name,
          followup_attachment: campaign.followup_attachment || '',
          second_followup_attachment: campaign.second_followup_attachment || '',
          invites_volume:
            campaign.invites_volume ||
            (!['acquisition_message_request', 'acquisition_inmail'].includes(campaign.type) ? '1' : '0'),
          third_followup_message: campaign.third_followup_message,
          third_followup_days: campaign.third_followup_days || defaultDaysDelay,
          third_followup_attachment: campaign.third_followup_attachment || '',
          tracking_link_prefix: tracking_link_prefix,
          add_attribution: campaign.add_attribution,
          isDuplicatedCampaign: campaign.isDuplicatedCampaign,
          shouldShowWhitelabelSignature: client.signature_enabled,
          linkedin_basic_feature: client?.linkedin_basic_feature,
          instantly_campaign_ids: campaign.instantly_campaign_ids || [],
          instantly_tags_ids: campaign.instantly_tags_ids || [],
          like_receiver_post: campaign.like_receiver_post,
          audience_list_id: campaign.audience_list_id,
          audience_list_filters_ids: campaign.audience_list_filters_ids || [],
          enrichment_id: campaign.enrichment_id,
          enrichment_filters_ids: campaign.enrichment_filters_ids || [],
          is_sn_inbox_type: campaign.is_sn_inbox_type ?? true,
          instantly_campaigns: instantly_campaigns || [],
        }}
        enableReinitialize={true}
        validationSchema={AcquisitionSchema}
        onSubmit={handleSubmit}
      >
        {({values, touched, errors, dirty, handleSubmit, setFieldValue}) => (
          <Form onFinish={handleSubmit} layout="vertical">
            <div style={{position: 'relative'}}>
              {!!campaign.archived && (
                <div className="view-only-overlay">
                  <span className="view-only-overlay__text">
                    <EyeOutlined /> View only
                  </span>
                </div>
              )}
              <FormRow>
                <Form.Item
                  label="Name*:"
                  validateStatus={errors.name && touched.name ? 'error' : ''}
                  hasFeedback
                  help={errors.name && touched.name ? errors.name?.toString() : ''}
                >
                  <Input
                    name="name"
                    value={values.name}
                    placeholder="Type the name of your campaign"
                    onChange={ev => setFieldValue('name', ev.target.value)}
                  />
                </Form.Item>
              </FormRow>
              {shouldShow && (
                <FormRow>
                  <Form.Item
                    label={
                      <span style={{display: 'flex', alignItems: 'center'}}>
                        Source*:
                        <span style={{marginLeft: 5, cursor: 'pointer'}}>
                          <InfoIcon
                            message={
                              <>
                                <span>{STRINGS.tooltip.nurturing_type_url}</span>
                                <br />
                                <span>{STRINGS.tooltip.nurturing_type_csv}</span>
                              </>
                            }
                          />
                        </span>
                      </span>
                    }
                    help={getSelectedTypeWarningIfNeeded(
                      selectedType,
                      client,
                      isInstantlyVerified,
                      rb2bLatestEvent,
                      setHeaderModal
                    )}
                  >
                    <Select
                      disabled={!values.isDuplicatedCampaign && isCampaignExist}
                      onChange={value => handleSwitchCampaignType(value, setFieldValue)}
                      value={selectedType}
                    >
                      <OptGroup label="LinkedIn" key="linkedin">
                        <Option key="acquisition" disabled={client.campaign_sources_limited}>
                          Sales Navigator Search
                        </Option>
                        {client?.linkedin_basic_feature && (
                          <Option key="acquisition_basic">Regular LinkedIn Search</Option>
                        )}
                        <Option key="acquisition_csv_liked_post">LinkedIn Post Likers</Option>
                      </OptGroup>
                      <OptGroup label="Import" key="import">
                        <Option key="acquisition_csv">CSV file</Option>
                      </OptGroup>
                      <OptGroup label="Integrations" key="integrations">
                        {client.instantly_feature && client.instantly_visitor_feature_enabled && (
                          <Option disabled={isInstantlyCampaignExist} key={CampaignTypes.AcquisitionCsvInstantly}>
                            <Tooltip
                              title={getIntegratedCampaignTooltip(
                                isInstantlyCampaignExist && !isCampaignExist,
                                CampaignTypes.AcquisitionCsvInstantly
                              )}
                            >
                              <div>Website Visitors via Instantly</div>
                            </Tooltip>
                          </Option>
                        )}
                        {client.rb2b_feature_enabled && (
                          <Option disabled={isRb2bCampaignExist} key={CampaignTypes.AcquisitionCsvRb2b}>
                            <Tooltip
                              title={getIntegratedCampaignTooltip(
                                isRb2bCampaignExist && !isCampaignExist,
                                CampaignTypes.AcquisitionCsvRb2b
                              )}
                            >
                              <div>Website Visitors via RB2B</div>
                            </Tooltip>
                          </Option>
                        )}
                        {client.asksquid_feature_enabled && brandConfig.squidFeature && (
                          <Option disabled={isSquidCampaignExist} key={CampaignTypes.AcquisitionCsvSquid}>
                            <Tooltip
                              title={getIntegratedCampaignTooltip(
                                isSquidCampaignExist && !isCampaignExist,
                                CampaignTypes.AcquisitionCsvSquid
                              )}
                            >
                              <div>Website Visitors via Squid</div>
                            </Tooltip>
                          </Option>
                        )}
                        <Option key={CampaignTypes.AcquisitionCsvVector}>Website Visitors via Vector</Option>
                        <Option key={CampaignTypes.AcquisitionCsvApi}>LinkedIn Profiles URLs via API</Option>
                        <Option key={CampaignTypes.AcquisitionCsvWebhook}>LinkedIn Profiles URLs via Webhook</Option>
                        <Option key={CampaignTypes.AcquisitionCsvZapier}>Leads via Zapier (7,000+ apps)</Option>
                      </OptGroup>

                      {isPaidClient(client) && (
                        <OptGroup label="Leads" key="audience_list">
                          <Option key={CampaignTypes.AcquisitionCsvAudienceList}>Audience List</Option>
                          <Option key={CampaignTypes.AcquisitionCsvEnrichment}>Enrichment List</Option>
                        </OptGroup>
                      )}
                    </Select>
                  </Form.Item>
                </FormRow>
              )}

              {
                {
                  acquisition: (
                    <FormRow>
                      {isAdmin && <LeadFiltrationField />}
                      <Form.Item
                        label={<SearchFilterLabel basic={selectedType === CONSTANTS.campaignTypes.acquisition_basic} />}
                        validateStatus={errors.filters && touched.filters ? 'error' : ''}
                        hasFeedback
                        help={errors.filters && touched.filters ? errors.filters?.toString() : ''}
                      >
                        <Input
                          name="filters"
                          placeholder={
                            selectedType === CONSTANTS.campaignTypes.acquisition_basic
                              ? STRINGS.placeholder.li_filter_url
                              : STRINGS.placeholder.filter_url
                          }
                          value={values.filters}
                          onBlur={() => onParseSearchUrl(values.filters)}
                          onChange={ev => {
                            setFieldValue('filters', ev.target.value);
                            setIsFiltersChanged(true);
                          }}
                        />
                      </Form.Item>
                    </FormRow>
                  ),
                  acquisition_csv: (
                    <FormRow>
                      <Form.Item
                        label={
                          <span style={{display: 'flex', alignItems: 'center'}}>
                            <span>
                              CSV file*:
                              {!client.csv_campaigns_feature && (
                                <span style={{marginLeft: 5, cursor: 'pointer'}}>
                                  <LockIcon onClick={onClickCsvRequest} message={STRINGS.message_unlock_feature} />
                                </span>
                              )}
                            </span>
                            <span style={{marginLeft: 5, cursor: 'pointer'}}>
                              <InfoIcon message={STRINGS.tooltip.csv} />
                            </span>
                          </span>
                        }
                        validateStatus={errors.csv && touched.csv ? 'error' : ''}
                        hasFeedback
                        help={errors.csv && touched.csv ? errors.csv?.toString() : ''}
                      >
                        <CsvInput
                          setFieldValue={setFieldValue}
                          disabled={client.csv_campaigns_feature ? canUpdateCSVFile : !client.csv_campaigns_feature}
                          csvFile={values.csv}
                          csvName={values.csv_name}
                          link={getCsvFileLink()}
                        />
                      </Form.Item>
                    </FormRow>
                  ),
                  acquisition_via_import: (
                    <FormRow>
                      <Form.Item
                        label={<SearchFilterLabel basic={false} />}
                        validateStatus={errors.filters && touched.filters ? 'error' : ''}
                        hasFeedback
                        help={errors.filters && touched.filters ? errors.filters?.toString() : ''}
                      >
                        <Input
                          name="filters"
                          placeholder={
                            selectedType === CONSTANTS.campaignTypes.acquisition_basic
                              ? STRINGS.placeholder.li_filter_url
                              : STRINGS.placeholder.filter_url
                          }
                          value={values.filters}
                          onBlur={() => onParseSearchUrl(values.filters)}
                          onChange={ev => {
                            setFieldValue('filters', ev.target.value);
                            setIsFiltersChanged(true);
                          }}
                        />
                      </Form.Item>
                    </FormRow>
                  ),
                  acquisition_message_request: (
                    <FormRow>
                      <Form.Item
                        label={<SearchFilterLabel basic={false} />}
                        validateStatus={errors.filters && touched.filters ? 'error' : ''}
                        hasFeedback
                        help={errors.filters && touched.filters ? errors.filters?.toString() : ''}
                      >
                        <Input
                          name="filters"
                          placeholder={STRINGS.placeholder.filter_url}
                          value={values.filters}
                          onBlur={() => onParseSearchUrl(values.filters)}
                          onChange={ev => {
                            setFieldValue('filters', ev.target.value);
                            setIsFiltersChanged(true);
                          }}
                        />
                      </Form.Item>
                    </FormRow>
                  ),
                  [CampaignTypes.AcquisitionInmail]: (
                    <>
                      <FormRow>
                        <Form.Item
                          label="Inmail Subject*:"
                          validateStatus={errors.in_mail_subject && touched.in_mail_subject ? 'error' : ''}
                          hasFeedback
                          help={
                            errors.in_mail_subject && touched.in_mail_subject ? errors.in_mail_subject?.toString() : ''
                          }
                        >
                          <Input
                            name="in_mail_subject"
                            value={values.in_mail_subject}
                            placeholder="Type the subject of your campaign"
                            onChange={ev => setFieldValue('in_mail_subject', ev.target.value)}
                          />
                        </Form.Item>
                      </FormRow>
                      <FormRow>
                        <Form.Item
                          label={<SearchFilterLabel basic={false} />}
                          validateStatus={errors.filters && touched.filters ? 'error' : ''}
                          hasFeedback
                          help={errors.filters && touched.filters ? errors.filters?.toString() : ''}
                        >
                          <Input
                            name="filters"
                            placeholder={STRINGS.placeholder.filter_url}
                            value={values.filters}
                            onBlur={() => onParseSearchUrl(values.filters)}
                            onChange={ev => {
                              setFieldValue('filters', ev.target.value);
                              setIsFiltersChanged(true);
                            }}
                          />
                        </Form.Item>
                      </FormRow>
                    </>
                  ),
                  [CampaignTypes.AcquisitionCsvWebhook]: (
                    <>
                      <FormRow>
                        <Form.Item
                          label="URL data extractor*:"
                          validateStatus={
                            errors.linkedin_url_data_extractor_string && touched.linkedin_url_data_extractor_string
                              ? 'error'
                              : ''
                          }
                          hasFeedback
                          help={
                            errors.linkedin_url_data_extractor_string && touched.linkedin_url_data_extractor_string
                              ? errors.linkedin_url_data_extractor_string?.toString()
                              : ''
                          }
                        >
                          <Input.TextArea
                            rows={3}
                            name="linkedin_url_data_extractor_string"
                            value={values.linkedin_url_data_extractor_string}
                            placeholder="This will be a dot separated string that will let us know how to extract LinkedIn URL from the data payload. Example: 'payload.linkedInUrl'"
                            onChange={ev => setFieldValue('linkedin_url_data_extractor_string', ev.target.value)}
                          />
                        </Form.Item>
                      </FormRow>
                      {values.webhook_token && (
                        <FormRow>
                          <Form.Item label="Your unique webhook url:" className="acquisition-webhook-token">
                            <Input
                              name="webhook_token"
                              value={`https://api.cuegrowth.ai/wh/${values.webhook_token}/`}
                              disabled
                              addonAfter={
                                <CopyOutlined
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `https://api.cuegrowth.ai/wh/${values.webhook_token}/`
                                    );
                                    message.success('Link copied to clipboard');
                                  }}
                                />
                              }
                            />
                          </Form.Item>
                        </FormRow>
                      )}
                    </>
                  ),
                  [CampaignTypes.AcquisitionCsvVector]: (
                    <>
                      {values.webhook_token && (
                        <FormRow>
                          <Form.Item label="Your unique webhook url:" className="acquisition-webhook-token">
                            <Input
                              name="webhook_token"
                              value={`https://api.cuegrowth.ai/wh/${values.webhook_token}/`}
                              disabled
                              addonAfter={
                                <CopyOutlined
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `https://api.cuegrowth.ai/wh/${values.webhook_token}/`
                                    );
                                    message.success('Link copied to clipboard');
                                  }}
                                />
                              }
                            />
                          </Form.Item>
                        </FormRow>
                      )}
                    </>
                  ),
                  [CampaignTypes.AcquisitionCsvLikedPost]: (
                    <FormRow>
                      <Form.Item
                        label={'Post URL*:'}
                        validateStatus={errors.filters && touched.filters ? 'error' : ''}
                        hasFeedback
                        help={errors.filters && touched.filters ? errors.filters?.toString() : ''}
                      >
                        <Input
                          name="filters"
                          placeholder={
                            'https://www.linkedin.com/posts/rikypisano_outreach-bookdemo-outreachmistakes-activity-7295094252831682560-XBEj'
                          }
                          value={values.filters}
                          onChange={ev => {
                            setFieldValue('filters', ev.target.value);
                            setIsFiltersChanged(true);
                          }}
                        />
                      </Form.Item>
                    </FormRow>
                  ),
                  [CampaignTypes.AcquisitionCsvAudienceList]: (
                    <>
                      <FormRow>
                        <SelectAudienceList
                          isCampaignExist={isCampaignExist}
                          errors={errors}
                          value={values.audience_list_id}
                          onChange={(value: string) => setFieldValue('audience_list_id', value)}
                          loadMoreLeadLists={loadMoreLeadLists}
                        />
                      </FormRow>

                      {values.audience_list_id && (
                        <FormRow>
                          <SelectAudienceListFilters
                            isCampaignExist={isCampaignExist}
                            errors={errors}
                            audienceListId={values.audience_list_id}
                            value={values.audience_list_filters_ids}
                            onChange={(value: string[]) => setFieldValue('audience_list_filters_ids', value)}
                            getFilters={getLeadFilters}
                            clearFilters={clearFilters}
                          />
                        </FormRow>
                      )}
                    </>
                  ),
                  [CampaignTypes.AcquisitionCsvEnrichment]: (
                    <React.Fragment key="acquisition-enrichment">
                      <FormRow>
                        <SelectEnrichmentList
                          isCampaignExist={isCampaignExist}
                          errors={errors}
                          value={values.enrichment_id}
                          onChange={(value: string) => setFieldValue('enrichment_id', value)}
                          loadMoreEnrichmentLists={loadMoreEnrichmentLists}
                        />
                      </FormRow>

                      {values.enrichment_id && (
                        <FormRow>
                          <SelectEnrichmentListFilters
                            isCampaignExist={isCampaignExist}
                            errors={errors}
                            enrichmentListId={values.enrichment_id}
                            value={values.enrichment_filters_ids}
                            onChange={(value: string[]) => setFieldValue('enrichment_filters_ids', value)}
                            getFilters={getEnrichmentFilters}
                            clearFilters={clearEnrichmentFilters}
                          />
                        </FormRow>
                      )}
                    </React.Fragment>
                  ),
                }[selectedType === CONSTANTS.campaignTypes.acquisition_basic ? 'acquisition' : selectedType]
              }
              <UrlFilters
                data={campaignParsedFilters}
                loading={campaignParsedFiltersLoading}
                filtersSavedSearch={campaign?.filters_full_json}
              />
              {shouldShowCampaignStartTimeField(client) && (
                <FormRow>
                  <Form.Item
                    label={
                      <span>
                        Campaign start time:
                        <span style={{marginLeft: 5, cursor: 'pointer'}}>
                          <InfoIcon message={campaignStartTimeTooltip} />
                        </span>
                      </span>
                    }
                  >
                    <DatePicker
                      showTime
                      allowClear
                      style={{width: '100%'}}
                      format={CONSTANTS.main_date_format}
                      value={
                        values.start_time ? dayjs(moment(values.start_time, CONSTANTS.main_date_format).toDate()) : null
                      }
                      onChange={value =>
                        setFieldValue('start_time', value ? value.format(CONSTANTS.main_date_format) : null)
                      }
                      disabledDate={disablePastCampaignStartDate}
                      disabledTime={disablePastCampaignStartTime}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                      placeholder="Select start time"
                    />
                  </Form.Item>
                </FormRow>
              )}
              {isFiltersChanged && selectedType === CONSTANTS.campaignTypes.acquisition && (
                <div style={{marginTop: '-10px', marginBottom: '5px'}}>
                  {getAdvancedMessages(values.filters, errors, touched).map((message: string, index: number) => (
                    <span key={`${index}`} dangerouslySetInnerHTML={{__html: message}} />
                  ))}
                </div>
              )}
              {!errors.filters && selectedType === CONSTANTS.campaignTypes.acquisition_basic && (
                <Alert
                  style={{
                    marginBottom: 10,
                    marginTop: 5,
                  }}
                  message={STRINGS.warning.basic_linkedin_campaign_warning}
                  type="warning"
                />
              )}
              <FormRow>
                <div>
                  <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', paddingBottom: '8px'}}>
                    <span>
                      {selectedType === 'acquisition_message_request'
                        ? 'Message Request:'
                        : selectedType === 'acquisition_inmail'
                        ? 'Inmail Message:'
                        : 'Invite Message:'}
                      <span style={{marginLeft: 5, cursor: 'pointer'}}>
                        <InfoIcon message={getTooltipMessage(selectedType).message} />
                      </span>
                    </span>
                    <span>
                      <MessageLengthCount
                        text={values.message}
                        maxLength={getInviteMaxLength(selectedType, values.message)}
                      />{' '}
                      {client.signature_enabled && <InfoIcon message={STRINGS.info.signature_invite_length} />}
                    </span>
                  </div>
                  <MessageTextArea
                    name="message"
                    placeholder={
                      [
                        CONSTANTS.campaignTypes.acquisition_via_import,
                        CONSTANTS.campaignTypes.acquisition_basic,
                      ].includes(selectedType)
                        ? STRINGS.placeholder.acquisition_via_import_message
                        : STRINGS.placeholder.invite_message
                    }
                    setValue={value => setFieldValue('message', value)}
                    value={values.message}
                    disabled={[
                      CONSTANTS.campaignTypes.acquisition_via_import,
                      CONSTANTS.campaignTypes.acquisition_basic,
                    ].includes(selectedType)}
                    tooltip={
                      selectedType === CONSTANTS.campaignTypes.acquisition_basic
                        ? 'Customizing the invite message note is disabled and left blank to maximize the number of connection requests on regular LinkedIn. For a more personalized approach, we recommend using Sales Navigator as the source.'
                        : ''
                    }
                    errors={errors.message}
                    touched={touched.message}
                    insertTexts={CONSTANTS.message_variable_list}
                    additionalData={
                      isContainLink(values.message) && (
                        <span className="text-warning">
                          {selectedType === 'acquisition_inmail'
                            ? STRINGS.warning.inmail_message_links
                            : STRINGS.warning.invite_message_links}
                        </span>
                      )
                    }
                    templates={templates}
                    createTemplate={createTemplate}
                    className="acquisition-invite-message"
                    showWhitelabelSignature={client.signature_enabled}
                    onUpgradePlan={moveFromPromoPlan}
                    onSuggestMessageTemplate={
                      shouldShowSuggestionButton && campaignTypesWithUrl.includes(selectedType)
                        ? () => onSuggestMessageTemplate('message', values, setFieldValue, 'message')
                        : null
                    }
                  />
                </div>
              </FormRow>
              <FormRow>
                <MessageTextArea
                  label={
                    <span>
                      Followup Message*:
                      <span style={{marginLeft: 5, cursor: 'pointer'}}>
                        <InfoIcon message={getTooltipMessage(selectedType).followup_message} />
                      </span>
                    </span>
                  }
                  placeholder={STRINGS.placeholder.followup_message}
                  name="followup_message"
                  additionalData={
                    <div>
                      <Select
                        showSearch
                        style={{width: 100}}
                        placeholder="days"
                        optionFilterProp="children"
                        onChange={(value: any) => setFieldValue('followup_days', value)}
                        value={values.followup_days}
                        filterOption={(input: any, option: any) =>
                          `${option.props.children}`.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {day_options.map(({key, label}) => (
                          <Select.Option key={key} value={key}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>{' '}
                      days after <InfoIcon message={'Days after invite accepted.'} />
                    </div>
                  }
                  setValue={value => setFieldValue('followup_message', value)}
                  value={values.followup_message}
                  errors={errors.followup_message}
                  touched={touched.followup_message}
                  insertTexts={CONSTANTS.message_variable_list}
                  showAttachment={true}
                  setAttachment={value => beforeUploadAttachment(setFieldValue, 'followup_attachment', value)}
                  attachment={values.followup_attachment}
                  clientAttachments={clientAttachments}
                  deleteClientAttachment={deleteClientAttachment}
                  showWhitelabelSignature={client.signature_enabled}
                  onUpgradePlan={moveFromPromoPlan}
                  templates={templates}
                  createTemplate={createTemplate}
                  className="acquisition-followup-message"
                  onSuggestMessageTemplate={
                    shouldShowSuggestionButton
                      ? () => onSuggestMessageTemplate('followup', values, setFieldValue, 'followup_message')
                      : null
                  }
                />
              </FormRow>
              <FormRow>
                <MessageTextArea
                  label={
                    <span style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span>
                        Second Followup Message: <InfoIcon message={STRINGS.tooltip.second_followup_message} />
                      </span>
                      <RemoveFollowUpButton
                        onClick={() => handleSwitchTextarea(false, 'second_followup_message', setFieldValue)}
                      />
                    </span>
                  }
                  placeholder={STRINGS.placeholder.second_followup_message}
                  name="second_followup_message"
                  setValue={value => setFieldValue('second_followup_message', value)}
                  value={values.second_followup_message}
                  errors={errors.second_followup_message}
                  touched={touched.second_followup_message}
                  insertTexts={CONSTANTS.message_variable_list}
                  showAttachment={true}
                  setAttachment={value => beforeUploadAttachment(setFieldValue, 'second_followup_attachment', value)}
                  attachment={values.second_followup_attachment}
                  additionalData={
                    <div>
                      <Select
                        showSearch
                        style={{width: 80}}
                        placeholder="days"
                        optionFilterProp="children"
                        onChange={(value: any) => setFieldValue('second_followup_days', value)}
                        value={values.second_followup_days}
                        filterOption={(input: any, option: any) =>
                          `${option.props.children}`.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {day_options.map(({key, label}) => (
                          <Select.Option key={key} value={key}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>{' '}
                      days after <InfoIcon message={'Days after first followup.'} />
                    </div>
                  }
                  display={second_followup_message_visible}
                  clientAttachments={clientAttachments}
                  deleteClientAttachment={deleteClientAttachment}
                  showWhitelabelSignature={client.signature_enabled}
                  onUpgradePlan={moveFromPromoPlan}
                  templates={templates}
                  createTemplate={createTemplate}
                  className="acquisition-second-followup-message"
                  onSuggestMessageTemplate={
                    shouldShowSuggestionButton
                      ? () =>
                          onSuggestMessageTemplate('second_followup', values, setFieldValue, 'second_followup_message')
                      : null
                  }
                />
              </FormRow>
              <div
                className="textarea-label-wrapper"
                style={{display: second_followup_message_visible ? 'none' : 'block'}}
              >
                <label className="textarea-label">
                  <span
                    style={{cursor: 'pointer'}}
                    onClick={checked => {
                      handleSwitchTextarea(checked, 'second_followup_message', setFieldValue);
                    }}
                  >
                    <PlusOutlined style={{backgroundColor: brandConfig.primaryColor}} /> Add Additional Followup Message
                  </span>
                </label>
              </div>
              <FormRow>
                <MessageTextArea
                  label={
                    <span style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span>
                        Third Followup Message: <InfoIcon message={STRINGS.tooltip.third_followup_message} />
                      </span>
                      <RemoveFollowUpButton
                        onClick={() => handleSwitchTextarea(false, 'third_followup_message', setFieldValue)}
                      />
                    </span>
                  }
                  placeholder={STRINGS.placeholder.third_followup_message}
                  name="third_followup_message"
                  setValue={value => setFieldValue('third_followup_message', value)}
                  value={values.third_followup_message}
                  errors={errors.third_followup_message}
                  touched={touched.third_followup_message}
                  insertTexts={CONSTANTS.message_variable_list}
                  showAttachment={true}
                  setAttachment={value => beforeUploadAttachment(setFieldValue, 'third_followup_attachment', value)}
                  attachment={values.third_followup_attachment}
                  additionalData={
                    <div>
                      <Select
                        showSearch
                        style={{width: 80}}
                        placeholder="days"
                        optionFilterProp="children"
                        onChange={(value: any) => setFieldValue('third_followup_days', value)}
                        value={values.third_followup_days}
                        filterOption={(input: any, option: any) =>
                          `${option.props.children}`.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {day_options.map(({key, label}) => (
                          <Select.Option key={key} value={key}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>{' '}
                      days after <InfoIcon message={'Days after second followup.'} />
                    </div>
                  }
                  display={third_followup_message_visible}
                  clientAttachments={clientAttachments}
                  deleteClientAttachment={deleteClientAttachment}
                  showWhitelabelSignature={client.signature_enabled}
                  onUpgradePlan={moveFromPromoPlan}
                  templates={templates}
                  createTemplate={createTemplate}
                  className="acquisition-third-followup-message"
                  onSuggestMessageTemplate={
                    shouldShowSuggestionButton
                      ? () =>
                          onSuggestMessageTemplate('third_followup', values, setFieldValue, 'third_followup_message')
                      : null
                  }
                />
              </FormRow>
              <div
                className="textarea-label-wrapper"
                style={{
                  display: third_followup_message_visible || !second_followup_message_visible ? 'none' : 'block',
                }}
              >
                <label className="textarea-label">
                  <span
                    style={{cursor: 'pointer'}}
                    onClick={checked => {
                      handleSwitchTextarea(checked, 'third_followup_message', setFieldValue);
                    }}
                  >
                    <PlusOutlined style={{backgroundColor: brandConfig.primaryColor}} /> Add Additional Followup Message
                  </span>
                </label>
              </div>
              <AdvancedOptions
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                client={client}
                trackIntercomEvent={trackIntercomEvent}
                selectedType={selectedType}
                clientTags={clientTags}
                InstantlyFormItemContainer={InstantlyFormItemContainer}
                shouldShow={shouldShow}
              />
            </div>
            <div style={{padding: '0 20px 20px 20px'}}>
              <Row style={{justifyContent: 'flex-end'}}>
                {error && <span className="campaigns-edit-modal__error-message">{STRINGS.error_server_response}</span>}
                <Button onClick={dirty ? onClose : onCloseModals} style={{marginRight: '10px'}}>
                  Cancel
                </Button>
                <Button disabled={!!campaign.archived} loading={false} type="primary" htmlType="submit">
                  Save
                </Button>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </Spin>
  );
};
